import {
  mapSubcategoryToActivities,
  mapSubcategoryToTours,
} from 'data/filters';
import { Filter, SubOption } from 'model/Filters';
import { Location, ParseReturn } from 'model/UrlParsing';
import pluralize from 'pluralize';
import slugify from 'utils/slugify';
import { useI18n } from '~/stores/i18n';

import availableLanguages from '../../resources/languages.json';
import SearchUrlCompiler from './SearchUrlCompiler';
import SearchUrlMapper from './SearchUrlMapper';

export function compile(
  country: string | null,
  city: string | null,
  page: number,
  filters: Filter[] | null,
  extraQueryParams?: URLSearchParams | null,
): string {
  return SearchUrlCompiler.compile(
    country,
    city,
    page,
    SearchUrlMapper.map(filters),
    extraQueryParams,
  );
}

const possibleBasePaths = ['/s/', '/experiences/'];
export const extractSearchBasePath = (
  url: string,
  defaultBasePath = '/experiences/',
) => {
  return (
    possibleBasePaths.find((f) => url.indexOf(f) !== -1) || defaultBasePath
  );
};

function dropCategories(activeFilters: Filter[]): Filter[] {
  const categories = activeFilters.find(
    ({ uid }) => uid === 'activityType.activity',
  );
  const subCategories = activeFilters.find(
    ({ uid }) => uid === 'subcategory.items[]',
  ) || { subOptions: [] };

  const lookup = {
    tours: Object.keys(mapSubcategoryToTours),
    activities: Object.keys(mapSubcategoryToActivities),
  };

  if (categories) {
    categories.subOptions = categories.subOptions.filter(({ value }) => {
      const subcategoriesInCategory: string[] = lookup[value];
      const subCategoriesOptions: SubOption[] = subCategories.subOptions;
      const matching: SubOption[] = subCategoriesOptions.filter(({ value }) =>
        subcategoriesInCategory.includes(value),
      );
      // all subcategories are selected within category
      if (matching.length === subcategoriesInCategory.length) {
        // remove matching subcategories
        subCategories.subOptions = subCategoriesOptions.filter(
          (filter) => !matching.includes(filter),
        );
        return true;
      }
      return matching.length === 0;
    });
  }

  return activeFilters.filter((filter) => {
    if ([categories, subCategories].includes(filter)) {
      return filter.subOptions.length > 0;
    }
    return true;
  });
}

export function runParser(
  url: string,
  filters: Filter[],
  locations: Location[],
): ParseReturn {
  const [pathname = '', queryValue = ''] = url.split('?');
  const pathParts = pathname.toLowerCase().split('/');
  const lowercaseQuery = new URLSearchParams(queryValue.toLowerCase());
  const query = new URLSearchParams(queryValue);
  const page = parseInt(
    pathParts.find((part) => !Number.isNaN(parseInt(part, 10))) || '1',
    10,
  );
  const lang =
    pathParts.filter((el) => availableLanguages.indexOf(el) > -1)[0] ||
    useI18n.getState().lang;

  const setValue = (filter: Filter) => {
    if (filter.uid === 'adults' || filter.uid === 'children') {
      const queryValue = lowercaseQuery.get(filter.queryName);
      return queryValue ? parseInt(queryValue, 10) : undefined;
    }
    if (filter.value) {
      return decodeURIComponent(filter.value);
    }

    if (filter.subOptions.length === 0) {
      const filterValue = lowercaseQuery.get(filter.queryName);
      if (filterValue) {
        return decodeURIComponent(filterValue);
      }
    }

    return undefined;
  };

  let activeFilters: Filter[] = filters
    .map((filter) => ({
      id: filter.id,
      uid: filter.uid,
      name: filter.name,
      queryName: filter.queryName,
      searchWithOthersQueryName: filter.searchWithOthersQueryName,
      value: setValue(filter),
      subOptions: filter.subOptions
        .filter((subOption) => {
          const queryParams = lowercaseQuery.getAll(filter.queryName);
          return (
            pathParts.includes(subOption.value.toLowerCase()) ||
            (filter.queryName === 'language[]' &&
              pathParts.includes(decodeURIComponent(subOption.value))) ||
            pathParts.includes(pluralize(subOption.value.toLowerCase(), 1)) ||
            pathParts.includes(pluralize(subOption.value.toLowerCase(), 2)) ||
            queryParams.some(
              (queryVal) => encodeURIComponent(queryVal) === subOption.value,
            ) ||
            queryParams.some(
              (queryVal) =>
                encodeURIComponent(queryVal) === pluralize(subOption.value, 1),
            ) ||
            queryParams.some(
              (queryVal) =>
                encodeURIComponent(queryVal) === pluralize(subOption.value, 2),
            )
          );
        })
        .sort((a) =>
          pathParts.includes(a.value) ||
          pathParts.includes(pluralize(a.value, 1)) ||
          pathParts.includes(pluralize(a.value, 2))
            ? 0
            : 1,
        ),
    }))
    .filter((filter: Filter) => {
      return (
        filter.subOptions.length > 0 ||
        lowercaseQuery
          .getAll(filter.queryName)
          .includes(String(filter.value)) ||
        pathParts.includes(filter.value) ||
        (!!filter.value &&
          pathParts.includes(
            pluralize(filter.value.toString(), 1) ||
              pathParts.includes(pluralize(filter.value.toString(), 2)),
          ))
      );
    });

  activeFilters = dropCategories(activeFilters);

  const countryLocation = locations.find(({ country }) =>
    pathParts.includes(slugify(country, false)),
  );
  const cityLocation = countryLocation
    ? locations.find(({ country, city }) => {
        const countryIndex = pathParts.indexOf(slugify(country, false));
        const cityIndex = pathParts.lastIndexOf(slugify(city, false));
        return (
          countryIndex > -1 && cityIndex > -1 && cityIndex !== countryIndex
        );
      })
    : undefined;
  const country = countryLocation ? countryLocation.country : null;
  const city = cityLocation ? cityLocation.city : null;

  const unrecognizedQueryParams = new URLSearchParams();
  query.forEach((value, name) => {
    if (
      filters.filter((filter) => filter.queryName === name.toLowerCase())
        .length > 0
    ) {
      return;
    }
    unrecognizedQueryParams.append(name, value);
  });

  return {
    lang,
    country,
    city,
    activeFilters,
    page,
    unrecognizedQueryParams,
  };
}

export function parse(
  url: string,
  filters: Filter[],
  locations: Location[],
  basePath = '/experiences/',
): ParseReturn {
  const parsedFilters = runParser(url, filters, locations);
  const { lang, country, city, page, unrecognizedQueryParams, activeFilters } =
    parsedFilters;

  const redirectUrl = SearchUrlMapper.redirects(parsedFilters.activeFilters);
  if (redirectUrl) {
    return {
      lang,
      redirectUrl,
      country: null,
      city: null,
      activeFilters: null,
      page: 0,
      unrecognizedQueryParams: null,
    };
  }

  const expectedUrl = compile(
    country,
    city,
    page,
    activeFilters,
    unrecognizedQueryParams,
  );

  if (expectedUrl === url) {
    return {
      lang,
      country,
      city,
      activeFilters,
      page,
      unrecognizedQueryParams,
    };
  }

  return {
    lang,
    redirectUrl: `${basePath.slice(0, -1)}${expectedUrl}`,
    country: null,
    city: null,
    activeFilters: null,
    page: 0,
    unrecognizedQueryParams: null,
  };
}
